import {
  BoldPlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
} from "@platejs/basic-nodes/react";
import {
  FontBackgroundColorPlugin,
  FontColorPlugin,
  FontFamilyPlugin,
  FontSizePlugin,
  TextAlignPlugin,
} from "@platejs/basic-styles/react";
import { LinkPlugin } from "@platejs/link/react";
import {
  getListItemEntry,
  indentListItems,
  unindentListItems,
} from "@platejs/list-classic";
import {
  BulletedListPlugin,
  ListItemContentPlugin,
  ListItemPlugin,
  ListPlugin,
  NumberedListPlugin,
} from "@platejs/list-classic/react";
import { ImagePlugin } from "@platejs/media/react";
import { LinkFloatingToolbar } from "@redotech/redo-web/components/plate-ui/link-floating-toolbar";
import { LinkElement } from "@redotech/redo-web/components/plate-ui/link-node";
import {
  BulletedListElement,
  ListItemContentElement,
  ListItemElement,
  NumberedListElement,
} from "@redotech/redo-web/components/plate-ui/list-classic-node";
import { ImageElement } from "@redotech/redo-web/components/plate-ui/media-image-node";
import { transformLinkInput } from "@redotech/redo-web/components/plate-ui/transform-link-input";
import {
  BaseParagraphPlugin,
  KEYS,
  type PluginConfig,
  TrailingBlockPlugin,
} from "platejs";
import { createPlatePlugin, createTPlatePlugin } from "platejs/react";
import { createElement, type ReactElement } from "react";
import { parseImageNode } from "./image-node-deserializer";
import { ProductLinkPicker } from "./product-link-picker";

// Base (non-React) plugins for static HTML serialization
import {
  BaseBoldPlugin,
  BaseItalicPlugin,
  BaseStrikethroughPlugin,
  BaseUnderlinePlugin,
} from "@platejs/basic-nodes";
import {
  BaseFontBackgroundColorPlugin,
  BaseFontColorPlugin,
  BaseFontFamilyPlugin,
  BaseFontSizePlugin,
  BaseTextAlignPlugin,
} from "@platejs/basic-styles";
import { BaseLinkPlugin } from "@platejs/link";
import {
  BaseBulletedListPlugin,
  BaseListItemContentPlugin,
  BaseListItemPlugin,
  BaseListPlugin,
  BaseNumberedListPlugin,
} from "@platejs/list-classic";
import { BaseImagePlugin } from "@platejs/media";
import type { TImageElement, TLinkElement } from "platejs";
import { SlateElement } from "platejs/static";

type StaticElementProps = Parameters<typeof SlateElement>[0];

/**
 * Static HTML serializer for image nodes.
 *
 * `serializeHtml` (the email-body path) renders each node via the plugin's
 * static component and falls back to a generic `<div>` when none is registered
 * — which dumps the image's `url` as a `data-slate-url` attribute and ships the
 * email with an unrenderable Slate `<div>` instead of an image. (The plugin's
 * `parsers.html.serializer` is NOT consulted by `serializeHtml`, so it cannot
 * fix this.)
 *
 * Render the image *as* a top-level `<img>` (via SlateElement `as: "img"`),
 * mirroring StaticLinkElement's `as: "a"` — NOT a `<div class="slate-img">`
 * wrapping an `<img>`. The wrapper looked fine in email (mail clients ignore the
 * div) but was not round-trip-safe: deserializing `<div…><img/></div>` back into
 * an editor placed the image as an inline child of a block, which crashes Slate
 * ("Cannot get the leaf node at path [n,0]") wherever stored macro/draft html is
 * reloaded — e.g. applying a macro into the composer. A bare `<img>` deserializes
 * straight back into a Plate void image node. `src` is the node URL, later
 * rewritten to a `cid:` reference on the outbound path so it renders inline.
 */
function StaticImageElement(props: StaticElementProps): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const element = props.element as TImageElement & {
    alt?: string;
    width?: string | number;
    height?: string | number;
  };
  const url = typeof element.url === "string" ? element.url : "";
  return createElement(
    SlateElement,
    {
      ...props,
      as: "img",
      attributes: {
        ...props.attributes,
        src: url,
        alt: typeof element.alt === "string" ? element.alt : "",
        ...(element.width !== undefined ? { width: element.width } : {}),
        ...(element.height !== undefined ? { height: element.height } : {}),
      },
    },
    // `<img>` is a void element: pass `null` children explicitly. The `...props`
    // spread carries the void node's empty-text child; this children argument
    // overrides it so nothing is emitted inside the self-closing `<img>`.
    null,
  );
}

// Plate's stock image deserializer reads only `src`, dropping width/height, so a
// composer-resized signature image loses its size on HTML round-trips (HELP-460).
const imageHtmlDeserializer = {
  rules: [{ validNodeName: "IMG" }],
  parse: parseImageNode,
};

const ConfiguredBaseImagePlugin = BaseImagePlugin.withComponent(
  StaticImageElement,
).extend(() => ({
  parsers: { html: { deserializer: imageHtmlDeserializer } },
}));

/**
 * Static HTML serializer for link nodes.
 *
 * Mirrors {@link StaticImageElement}: without a static component, `serializeHtml`
 * falls back to a generic `<div>` and ships the email with `<div>text</div>`
 * instead of `<a href="…">text</a>`. The editor renders links via `LinkElement`
 * so the merchant sees a styled link as they type, but on send the static
 * serializer drops the `<a>` and recipients get plain text (PRO-1103).
 *
 * Registering this component makes the link serialize to a real `<a href>` with
 * `target` and a safe `rel` when `target="_blank"` is set, so outbound HTML
 * preserves the hyperlink end-to-end.
 */
function StaticLinkElement(props: StaticElementProps): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const element = props.element as TLinkElement;
  const url = typeof element.url === "string" ? element.url : "";
  const target =
    typeof element.target === "string" ? element.target : undefined;
  return createElement(
    SlateElement,
    {
      ...props,
      as: "a",
      attributes: {
        ...props.attributes,
        href: url,
        ...(target ? { target } : {}),
        // rel matches the runtime LinkElement (getLinkAttributes) — without
        // it, _blank links leak window.opener access to the destination.
        ...(target === "_blank" ? { rel: "noopener noreferrer" } : {}),
      },
    },
    props.children,
  );
}

const ConfiguredBaseLinkPlugin =
  BaseLinkPlugin.withComponent(StaticLinkElement);

// The core paragraph plugin has no static render target. Without this explicit
// target, Plate's email serializer falls back to a generic <div> for every
// paragraph, which loses the paragraph semantics mail clients use for spacing.
const ConfiguredBaseParagraphPlugin = BaseParagraphPlugin.configure({
  render: { as: "p" },
});

// `use-message-input-attachments` wires pasted/dropped image uploads through
// `editor.setOption(ImagePlugin, "uploadImage", ...)`. Plate's `setOption`
// rejects keys the plugin never declared, and `@platejs/media`'s ImagePlugin
// does not declare `uploadImage` as a runtime option (it is type-only), so the
// call throws `[OPTION_UNDEFINED]` and crashes the editor. Declare the option
// here so the runtime key exists for `setOption` to update.
const ConfiguredImagePlugin = ImagePlugin.withComponent(ImageElement)
  .configure({ options: { uploadImage: undefined } })
  .extend(() => ({
    parsers: { html: { deserializer: imageHtmlDeserializer } },
  }));

type FileDropConfig = PluginConfig<
  "fileDrop",
  {
    /**
     * Diverts a pure file drop/paste. Returns `true` if it claimed the files
     * (so the editor skips its default insert), `false` to fall through to
     * Slate — e.g. when drops are disabled for the current channel. A stable
     * dispatcher is wired here once at editor creation; it reads the live
     * per-channel handler from a ref, so the callback can change without
     * reconfiguring the plugin or running an effect.
     */
    onFiles?: (files: File[]) => boolean;
  }
>;

// A *pure* file drop/paste carries files and no `text/plain`. A rich
// web-content paste carries an image `File` alongside text/html, which we must
// leave to deserialize as content. Mirrors `@platejs/media`'s `withImageUpload`.
function isPureFileDrop(text: string, fileCount: number): boolean {
  return text.length === 0 && fileCount > 0;
}

/**
 * Plate-native OS-file ingestion.
 *
 * Wraps the editor's `insertData` transform — the seam Slate invokes for BOTH
 * drop and paste — and diverts a pure file payload to the `onFiles` callback
 * instead of letting Slate (or the media plugin) insert the file as text/an
 * inline node. This is the same mechanism `@platejs/media`'s `withImageUpload`
 * uses to catch pasted images; routing non-image attachments out to the
 * composer's chip tray is the gap this fills.
 *
 * `useMessageEditor` configures the `onFiles` option once with a stable
 * dispatcher that reads the live per-channel handler from a ref, so no DOM
 * listeners and no effect are involved.
 */
export const FileDropPlugin = createTPlatePlugin<FileDropConfig>({
  key: "fileDrop",
  options: { onFiles: undefined },
}).overrideEditor(({ getOptions, tf: { insertData } }) => ({
  transforms: {
    insertData(dataTransfer) {
      const { onFiles } = getOptions();
      const text = dataTransfer.getData("text/plain");
      const { files } = dataTransfer;
      if (
        onFiles &&
        isPureFileDrop(text, files.length) &&
        onFiles(Array.from(files))
      ) {
        return;
      }
      insertData(dataTransfer);
    },
  },
}));

// Reading marks while the selection sits on a void node — e.g. a just-inserted
// or selected inline image — makes Plate's `editor.api.marks()` throw
// `undefined is not iterable`. Every mark-reading toolbar button (bold, italic,
// underline, strikethrough, color, ...) calls `editor.api.mark(...)` during
// render, so a single image insert crashes the whole toolbar. Guard the
// mark-reading API at the editor level so a void selection reports "no marks"
// instead of throwing.
// Inserts 2 spaces on Tab when not in a list and no autocomplete is pending.
// event.defaultPrevented guard lets ListPlugin and capture-phase autocomplete take priority.
const TabIndentPlugin = createPlatePlugin({
  key: "tabIndent",
  handlers: {
    onKeyDown: ({ editor, event }) => {
      if (event.key !== "Tab" || event.defaultPrevented || event.shiftKey) {
        return;
      }
      event.preventDefault();
      editor.tf.insertText("  ");
    },
  },
});

// Destructure the originals: `api` is the live `editor.api` this override is
// merged into, so calling `api.marks()` here would recurse into itself.
const SafeMarkReadingPlugin = createPlatePlugin({
  key: "safeMarkReading",
}).overrideEditor(({ api: { mark, marks } }) => ({
  api: {
    marks: () => {
      try {
        return marks();
      } catch {
        return null;
      }
    },
    mark: (key) => {
      try {
        return mark(key);
      } catch {
        return undefined;
      }
    },
  },
}));

const markPlugins = [
  BoldPlugin,
  ItalicPlugin,
  UnderlinePlugin,
  StrikethroughPlugin,
];

const linkPlugin = LinkPlugin.configure({
  options: {
    // Default `is-url` validation rejects scheme-less input, so a bare
    // `google.com` makes the floating toolbar's Apply button a no-op. Prepend
    // a protocol before validation so normal typed/pasted links insert.
    transformInput: transformLinkInput,
  },
  render: {
    node: LinkElement,
    afterEditable: () =>
      createElement(LinkFloatingToolbar, {
        insertExtension: (props) => createElement(ProductLinkPicker, props),
      }),
  },
});

export const emailEditorPlugins = [
  SafeMarkReadingPlugin,
  ...markPlugins,
  FontFamilyPlugin,
  FontSizePlugin,
  FontColorPlugin,
  FontBackgroundColorPlugin,
  TextAlignPlugin.configure({
    inject: {
      nodeProps: {
        defaultNodeValue: "start",
        nodeKey: "align",
        styleKey: "textAlign",
        validNodeValues: ["start", "left", "center", "right", "end", "justify"],
      },
      targetPlugins: [KEYS.p],
    },
  }),
  ConfiguredImagePlugin,
  linkPlugin,
  ListPlugin.configure({
    handlers: {
      // ListPlugin's withList.tab uses editor.api.some which only checks nodes
      // at the cursor, not ancestors — cursor in lic never matches the parent
      // li. getListItemEntry is ancestor-aware and finds it correctly.
      onKeyDown: ({ editor, event }) => {
        if (event.key !== "Tab" || event.defaultPrevented) {
          return;
        }
        if (!getListItemEntry(editor)) {
          return;
        }
        event.preventDefault();
        if (event.shiftKey) {
          unindentListItems(editor);
        } else {
          indentListItems(editor);
        }
      },
    },
  }),
  BulletedListPlugin.withComponent(BulletedListElement),
  NumberedListPlugin.withComponent(NumberedListElement),
  ListItemPlugin.withComponent(ListItemElement),
  ListItemContentPlugin.withComponent(ListItemContentElement),
  TabIndentPlugin,
  // Images are void blocks; when one is the last node there is no editable
  // block after it for the caret to land in, so typing after an inserted or
  // pasted image is impossible. Keep a trailing empty paragraph at the end of
  // the editor so there is always somewhere to continue typing.
  TrailingBlockPlugin.configure({ options: { type: KEYS.p } }),
];

export const chatEditorPlugins = [
  SafeMarkReadingPlugin,
  ...markPlugins,
  linkPlugin,
  ConfiguredImagePlugin,
  TabIndentPlugin,
  TrailingBlockPlugin.configure({ options: { type: KEYS.p } }),
];

export const editorToolbarMode = ["email", "chat"] as const;
export type EditorToolbarMode = (typeof editorToolbarMode)[number];

export function getPluginsForMode(mode: EditorToolbarMode) {
  switch (mode) {
    case "email":
      return emailEditorPlugins;
    case "chat":
      return chatEditorPlugins;
  }
}

// Base plugin lists for static HTML serialization
const baseMarkPlugins = [
  BaseBoldPlugin,
  BaseItalicPlugin,
  BaseUnderlinePlugin,
  BaseStrikethroughPlugin,
];

export const emailBasePlugins = [
  ConfiguredBaseParagraphPlugin,
  ...baseMarkPlugins,
  BaseFontFamilyPlugin,
  BaseFontSizePlugin,
  BaseFontColorPlugin,
  BaseFontBackgroundColorPlugin,
  BaseTextAlignPlugin.configure({
    inject: {
      nodeProps: {
        defaultNodeValue: "start",
        nodeKey: "align",
        styleKey: "textAlign",
        validNodeValues: ["start", "left", "center", "right", "end", "justify"],
      },
      targetPlugins: [KEYS.p],
    },
  }),
  ConfiguredBaseImagePlugin,
  ConfiguredBaseLinkPlugin,
  BaseListPlugin,
  BaseBulletedListPlugin,
  BaseNumberedListPlugin,
  BaseListItemPlugin,
  BaseListItemContentPlugin,
];

export const chatBasePlugins = [
  ConfiguredBaseParagraphPlugin,
  ...baseMarkPlugins,
  ConfiguredBaseLinkPlugin,
  ConfiguredBaseImagePlugin,
];

export function getBasePluginsForMode(mode: EditorToolbarMode) {
  switch (mode) {
    case "email":
      return emailBasePlugins;
    case "chat":
      return chatBasePlugins;
  }
}
